import { toUsStateCode } from "../../utils/usStates.mjs";
import { customerDateOfBirthSchema } from "../../utils/dateOfBirth.mjs";
import { PiiCategory, RedactStrategy, meta } from "../meta.mjs";
import { BRIDGE_PATIENT_CUSTOMER_SYNC_FIELDS, pickBridgePatientCustomerSyncFields } from "../vendors/bridge/syncTargets.mjs";
import { z as z$1 } from "zod";

// src/models/consultation/bookingDemographics.ts
const bookingDemographicsSchema = z$1.object({
	firstName: meta(z$1.string().trim().min(1), { pii: { category: "name" } }),
	lastName: meta(z$1.string().trim().min(1), { pii: { category: "name" } }),
	dateOfBirth: meta(customerDateOfBirthSchema, { pii: {
		category: "date",
		redact: "truncateYear"
	} }),
	email: meta(z$1.string().trim().min(1), { pii: { category: "email" } }),
	mobilePhone: meta(z$1.string().optional(), { pii: { category: "phone" } }),
	state: z$1.string().trim().min(1)
});
function buildBookingDemographicsFromCustomer(customer) {
	return bookingDemographicsSchema.parse({
		firstName: customer.firstName,
		lastName: customer.lastName,
		dateOfBirth: customer.dateOfBirth,
		email: customer.email,
		mobilePhone: customer.mobilePhone,
		state: customer.stateOfResidence
	});
}
function normalizeEmail(value) {
	return (value ?? "").trim().toLowerCase();
}
function normalizePhone(value) {
	if (!value) return "";
	const digits = value.replace(/\D/g, "");
	return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
}
function normalizeDateOfBirth(value) {
	return (value ?? "").slice(0, 10);
}
function syncFieldRecordsEqual(left, right) {
	for (const key of BRIDGE_PATIENT_CUSTOMER_SYNC_FIELDS) {
		if (key === "email") {
			if (normalizeEmail(String(left[key] ?? "")) !== normalizeEmail(String(right[key] ?? ""))) return false;
			continue;
		}
		if (key === "mobilePhone") {
			if (normalizePhone(String(left[key] ?? "")) !== normalizePhone(String(right[key] ?? ""))) return false;
			continue;
		}
		if (key === "dateOfBirth") {
			if (normalizeDateOfBirth(String(left[key] ?? "")) !== normalizeDateOfBirth(String(right[key] ?? ""))) return false;
			continue;
		}
		if (String(left[key] ?? "").trim() !== String(right[key] ?? "").trim()) return false;
	}
	return true;
}
/** Compare booking snapshot sync fields to current customer sync fields (self-pay). */
function bookingDemographicsMatchCustomer(booking, customer) {
	return syncFieldRecordsEqual(pickBridgePatientCustomerSyncFields(booking), pickBridgePatientCustomerSyncFields(customer));
}
/** Compare booking snapshot sync fields to Bridge L3.5 patient target (insured). */
function bookingDemographicsMatchBridgePatientTarget(booking, target) {
	return syncFieldRecordsEqual(pickBridgePatientCustomerSyncFields(booking), pickBridgePatientCustomerSyncFields(target));
}
function resolveProfileStateCode(profile) {
	return toUsStateCode(profile.stateOfResidence ?? profile.state) ?? "";
}
/** Compare Bridge sync demographics + state between two customer/profile snapshots. */
function customerBridgeProfileDemographicsEqual(baseline, submitted) {
	if (!syncFieldRecordsEqual(pickBridgePatientCustomerSyncFields(baseline), pickBridgePatientCustomerSyncFields(submitted))) return false;
	return resolveProfileStateCode(baseline) === resolveProfileStateCode(submitted);
}

export { bookingDemographicsMatchBridgePatientTarget, bookingDemographicsMatchCustomer, bookingDemographicsSchema, buildBookingDemographicsFromCustomer, customerBridgeProfileDemographicsEqual };
//# sourceMappingURL=bookingDemographics.mjs.map